// DocumentUpload — drag-drop card + list for property documents.
import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  ["lease", "Lease"],
  ["inspection", "Inspection"],
  ["receipt", "Receipt"],
  ["insurance", "Insurance"],
  ["tax", "Tax"],
  ["appraisal", "Appraisal"],
  ["photo", "Photo"],
  ["other", "Other"],
];

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n}B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1048576).toFixed(1)}MB`;
}

export default function DocumentUpload({ propertyId, documents, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docType, setDocType] = useState("other");
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", docTitle || file.name);
      fd.append("type", docType);
      await api.post(`/manage/properties/${propertyId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      setDocTitle("");
      setDocType("other");
      if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  const download = async (id, filename) => {
    try {
      const { data } = await api.get(`/manage/documents/${id}`);
      // Open in new tab; the server validates the signed token & streams.
      const a = window.document.createElement("a");
      a.href = data.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = filename || data.filename || "document";
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
    } catch (err) {
      toast.error("Could not generate download URL");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document? This removes the file from disk too.")) return;
    try {
      await api.delete(`/manage/documents/${id}`);
      toast.success("Document deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-5">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-6 transition hover:border-indigo-300"
        data-testid="doc-upload-zone"
      >
        <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-end sm:text-left">
          <Upload className="h-8 w-8 text-slate-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700">Drop a file here, or browse</p>
            <p className="text-xs text-slate-500">PDF / image / docx · max 25MB</p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label htmlFor="doc-title">Title</Label>
            <Input id="doc-title" data-testid="doc-title" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="e.g. 2026 lease — Jane Doe" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <input
              ref={fileRef}
              type="file"
              data-testid="doc-file-input"
              className="hidden"
              accept="application/pdf,image/jpeg,image/png,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button
              type="button"
              className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="doc-upload-button"
            >
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Choose file
            </Button>
          </div>
        </div>
      </div>

      {documents?.length > 0 ? (
        <ul className="grid gap-2 sm:grid-cols-2" data-testid="doc-list">
          {documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:shadow-md"
              data-testid={`doc-row-${d.id}`}
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
                <FileText className="h-4 w-4 text-indigo-700" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{d.title}</p>
                <p className="text-xs text-slate-500">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{d.type}</span>
                  <span className="ml-2">{fmtBytes(d.size)}</span>
                </p>
              </div>
              <button onClick={() => download(d.id, d.filename)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-600" title="Download">
                <Download className="h-4 w-4" />
              </button>
              <button onClick={() => remove(d.id)} className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">No documents uploaded yet.</p>
      )}
    </div>
  );
}
